/**
 * /finance/payroll — Internal Crew Payroll
 * ────────────────────────────────────────
 * Steve's Friday-ahead check: who logged hours this period, how much
 * burdened labor cost is going out the door, and which jobs are
 * consuming the labor budget.
 *
 * Reads:   AppDataContext.laborActualRecords  (canonical append-only)
 *          AppDataContext.jobs                (for job labels)
 *          internalLaborRates                 (canonical loaded rates)
 *
 * Writes:  NONE. Pay-run release stays at /finance/pay-runs.
 *          Hour capture stays in the Crew Lead Closeout dialog.
 *
 * Role gate: financial visibility required.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Users, ClipboardList, TrendingUp } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitShell";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";
import { listLoadedRates, type InternalCrewRole } from "@/data/finance/payRun/internalLaborRates";
import type { LaborActualRecord } from "@/data/finance/payRun/laborActuals";

const MS_DAY = 86_400_000;
const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}K`
  : `$${Math.round(n).toLocaleString()}`;
const fmtHours = (h: number) => `${h.toFixed(1)}h`;

type Period = "week" | "biweekly" | "mtd";

const PERIOD_LABEL: Record<Period, string> = {
  week: "This week",
  biweekly: "Pay period (14d)",
  mtd: "Month-to-date",
};

function periodStart(p: Period): number {
  const now = new Date();
  if (p === "week") return now.getTime() - 7 * MS_DAY;
  if (p === "biweekly") return now.getTime() - 14 * MS_DAY;
  // MTD
  return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
}

/**
 * Reduce append-only history to latest non-superseded record per job.
 * Mirrors `getEffectiveLaborActual` but returns all jobs in the window.
 */
function reduceLatestPerJob(records: LaborActualRecord[]): LaborActualRecord[] {
  const superseded = new Set<string>();
  for (const r of records) if (r.supersedesId) superseded.add(r.supersedesId);
  // Group by job, take most recent non-superseded.
  const byJob = new Map<string, LaborActualRecord>();
  for (const r of records) {
    if (superseded.has(r.id)) continue;
    const existing = byJob.get(r.jobId);
    if (!existing || new Date(r.enteredAt).getTime() > new Date(existing.enteredAt).getTime()) {
      byJob.set(r.jobId, r);
    }
  }
  return [...byJob.values()];
}

export default function PayrollPage() {
  const appData = useAppData();
  const { jobs } = appData;
  const laborActualRecords: any[] = (appData as any).laborActualRecords ?? [];
  const fin$ = useFinancialPermissions();
  const [period, setPeriod] = useState<Period>("biweekly");

  const periodWindow = useMemo(() => {
    const start = periodStart(period);
    return laborActualRecords.filter((r) => new Date(r.enteredAt).getTime() >= start);
  }, [laborActualRecords, period]);

  const effective = useMemo(() => reduceLatestPerJob(periodWindow), [periodWindow]);

  const totals = useMemo(() => {
    let dollars = 0, hours = 0;
    const enteredBy = new Set<string>();
    for (const r of effective) {
      dollars += r.actualDollars;
      for (const l of r.lines) hours += l.hours;
      enteredBy.add(r.enteredBy);
    }
    return { dollars, hours, jobs: effective.length, crewLeads: enteredBy.size };
  }, [effective]);

  const byRole = useMemo(() => {
    const map = new Map<InternalCrewRole, { role: InternalCrewRole; hours: number; dollars: number }>();
    for (const r of effective) {
      for (const l of r.lines) {
        const row = map.get(l.role) ?? { role: l.role, hours: 0, dollars: 0 };
        row.hours += l.hours;
        row.dollars += l.dollars;
        map.set(l.role, row);
      }
    }
    return [...map.values()].sort((a, b) => b.dollars - a.dollars);
  }, [effective]);

  const byCrewLead = useMemo(() => {
    const map = new Map<string, { enteredBy: string; jobs: number; hours: number; dollars: number; lastAt: string }>();
    for (const r of effective) {
      const hours = r.lines.reduce((s, l) => s + l.hours, 0);
      const row = map.get(r.enteredBy) ?? {
        enteredBy: r.enteredBy, jobs: 0, hours: 0, dollars: 0, lastAt: r.enteredAt,
      };
      row.jobs += 1;
      row.hours += hours;
      row.dollars += r.actualDollars;
      if (new Date(r.enteredAt).getTime() > new Date(row.lastAt).getTime()) row.lastAt = r.enteredAt;
      map.set(r.enteredBy, row);
    }
    return [...map.values()].sort((a, b) => b.dollars - a.dollars);
  }, [effective]);

  const byJob = useMemo(() => {
    const jobLabel = new Map(jobs.map((j) => [j.id, j.customerName]));
    return effective
      .map((r) => ({
        jobId: r.jobId,
        label: jobLabel.get(r.jobId) ?? r.jobId,
        hours: r.lines.reduce((s, l) => s + l.hours, 0),
        dollars: r.actualDollars,
        enteredBy: r.enteredBy,
        enteredAt: r.enteredAt,
        source: r.source,
      }))
      .sort((a, b) => b.dollars - a.dollars);
  }, [effective, jobs]);

  const loadedRates = useMemo(() => listLoadedRates(), []);

  if (!fin$.viewFinancialSummary) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <ShieldAlert className="h-6 w-6 text-destructive mb-3" />
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-muted-foreground">Payroll visibility is restricted to finance roles.</p>
        </div>
      </CockpitPage>
    );
  }

  return (
    <CockpitPage>
      <header className="pb-3 border-b border-border/60 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Payroll · Internal Crew</h1>
          <p className="text-[12px] text-muted-foreground mt-1">
            {PERIOD_LABEL[period]} · {totals.jobs} job{totals.jobs === 1 ? "" : "s"} captured · {totals.crewLeads} crew lead{totals.crewLeads === 1 ? "" : "s"} reporting
          </p>
        </div>
        <div className="flex items-center gap-1 text-[12px] shrink-0">
          {(["week", "biweekly", "mtd"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 border ${period === p ? "border-primary/30 bg-primary/10 text-primary font-medium" : "border-border/60 text-muted-foreground hover:text-foreground"}`}
            >
              {p === "week" ? "Week" : p === "biweekly" ? "Pay period" : "MTD"}
            </button>
          ))}
        </div>
      </header>

      <FinanceNavStrip />

      {/* Period summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-border/60 divide-x divide-y md:divide-y-0 divide-border/60 bg-background">
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Burdened cost</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{fmtCompact(totals.dollars)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">wage + burden + overhead</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Hours logged</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{fmtHours(totals.hours)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">across {totals.jobs} job{totals.jobs === 1 ? "" : "s"}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Avg loaded rate</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">
            {totals.hours > 0 ? `$${(totals.dollars / totals.hours).toFixed(0)}/h` : "—"}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">weighted across roles</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Crew leads</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{totals.crewLeads}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">reporting this period</p>
        </div>
      </div>

      {/* Two-column: by-role + by-crew-lead */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* By role */}
        <div className="bg-background border border-border/60">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">By role</p>
            </div>
            <span className="text-[11px] text-muted-foreground tabular-nums">{byRole.length} role{byRole.length === 1 ? "" : "s"}</span>
          </div>
          {byRole.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">No labor logged in this period.</p>
          ) : (
            byRole.map((r) => {
              const rateRow = loadedRates.find((l) => l.role === r.role);
              const pct = totals.dollars > 0 ? Math.round((r.dollars / totals.dollars) * 100) : 0;
              return (
                <div key={r.role} className="px-4 py-3 border-b border-border/40 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-medium text-foreground">{rateRow?.label ?? r.role}</p>
                    <span className="text-[13px] font-semibold tabular-nums">{fmt(r.dollars)}</span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary/60" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                    {fmtHours(r.hours)} · ${rateRow?.loadedRate ?? "—"}/h loaded · {pct}% of period
                  </p>
                </div>
              );
            })
          )}
        </div>

        {/* By crew lead */}
        <div className="bg-background border border-border/60">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
            <div className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-muted-foreground" />
              <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">By crew lead</p>
            </div>
            <span className="text-[11px] text-muted-foreground tabular-nums">{byCrewLead.length} reporting</span>
          </div>
          {byCrewLead.length === 0 ? (
            <p className="px-4 py-8 text-center text-[13px] text-muted-foreground">No crew leads have closed out this period.</p>
          ) : (
            byCrewLead.map((c) => (
              <div key={c.enteredBy} className="px-4 py-3 border-b border-border/40 last:border-0 grid grid-cols-[1fr_90px_90px_90px] gap-3 items-center">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-foreground truncate">{c.enteredBy}</p>
                  <p className="text-[10px] text-muted-foreground">last: {new Date(c.lastAt).toLocaleDateString()}</p>
                </div>
                <span className="text-right text-[12px] text-muted-foreground tabular-nums">{c.jobs} job{c.jobs === 1 ? "" : "s"}</span>
                <span className="text-right text-[12px] tabular-nums">{fmtHours(c.hours)}</span>
                <span className="text-right text-[13px] font-semibold tabular-nums">{fmt(c.dollars)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* By job — drill detail */}
      <div className="bg-background border border-border/60">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Job detail · latest effective record per job</p>
          </div>
          <span className="text-[11px] text-muted-foreground tabular-nums">{byJob.length} job{byJob.length === 1 ? "" : "s"}</span>
        </div>
        <div className="grid grid-cols-[1fr_100px_90px_90px_120px] gap-4 px-4 py-2 border-b border-border/60 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
          <span>Job</span>
          <span className="text-right">Hours</span>
          <span className="text-right">Cost</span>
          <span className="text-right">Source</span>
          <span className="text-right">Captured</span>
        </div>
        {byJob.length === 0 ? (
          <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">No labor captured in this window. Crew leads log hours from the job closeout flow.</p>
        ) : (
          byJob.map((j) => (
            <div key={j.jobId} className="grid grid-cols-[1fr_100px_90px_90px_120px] gap-4 px-4 py-3 border-b border-border/40 last:border-0 hover:bg-muted/20 items-center">
              <div className="min-w-0">
                <Link to={`/jobs/${j.jobId}`} className="text-[13px] font-medium text-foreground hover:text-primary truncate block">
                  {j.label}
                </Link>
                <p className="text-[10px] text-muted-foreground">by {j.enteredBy}</p>
              </div>
              <span className="text-right text-[12px] tabular-nums">{fmtHours(j.hours)}</span>
              <span className="text-right text-[13px] font-semibold tabular-nums">{fmt(j.dollars)}</span>
              <span className="text-right text-[11px] text-muted-foreground">{j.source === "payroll_import" ? "imported" : "crew lead"}</span>
              <span className="text-right text-[11px] text-muted-foreground tabular-nums">{new Date(j.enteredAt).toLocaleDateString()}</span>
            </div>
          ))
        )}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Labor actuals are append-only. Corrections create a new entry that supersedes the prior one — the latest non-superseded record per job is shown here. Loaded rates include payroll burden and overhead allocation.
      </p>
    </CockpitPage>
  );
}
